import { Link } from 'react-router-dom';
import image2 from "../images/image2.jpeg";
import { Button, Grid, Box, Typography, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { Helmet } from 'react-helmet';
import { FamilyRestroom, EmojiPeople, AccessTime, SupportAgent } from '@mui/icons-material';

const AboutUs = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const values = [
        {
            icon: <FamilyRestroom sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Family-Founded',
            description: 'Founded by a family with over 25 years of combined caregiving experience.'
        },
        {
            icon: <EmojiPeople sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Compassionate Care',
            description: 'Personalized care with a focus on dignity, respect, and emotional well-being.'
        },
        {
            icon: <AccessTime sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: '24/7 Availability',
            description: 'Round-the-clock care, including holidays and weekends, for your peace of mind.'
        },
        {
            icon: <SupportAgent sx={{ fontSize: 40, color: '#1b8fc9' }} />,
            title: 'Caregiver Matching',
            description: 'Careful matching based on client needs, preferences, and compatibility.'
        }
    ];

    return (
        <div className='page-back'>
            <Helmet>
                <title>24/7 AssureCare - About Us</title>
                <meta name="description" content="Learn about 24/7 AssureCare LLC, a family-founded home care agency providing compassionate, personalized care for seniors and those with disabilities." />
                <meta name="keywords" content="home care, caregivers, senior care, disability care, personalized care, 24/7 care, family-founded, in-home assistance" />
            </Helmet>
            
            <Box className="container" sx={{ py: 2 }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h2" component="h1" sx={{ color: 'white', fontWeight: 'bold',mb: 2,fontSize: isMobile ? '2rem' : '2.8rem'}}>
                        GET TO KNOW US
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', maxWidth: '800px', margin: '0 auto' }}>
                        Family-Founded Care with Over 25 Years of Combined Experience
                    </Typography>
                </Box>

                {/* Main Content Section */}
                <Grid container spacing={4} alignItems="center" sx={{ mb: 8 }}>
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: 3, boxShadow: 3,height: '100%',p: 3}}>
                            <CardContent>
                                <Typography variant="h4" component="h4" sx={{ color: '#1b8fc9', mb: 3,fontWeight: 'bold'}}>
                                    Our Story
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, fontSize: '1.1rem' }}>
                                    As a company founded by a family with over 25 years of combined caregiving 
                                    experience, we understand that caring for a loved one can be a challenging 
                                    and emotional journey. That's why we're here to offer our compassionate 
                                    and professional caregiving services to families in need.
                                </Typography>
                                <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, fontSize: '1.1rem' }}>
                                    24/7 Assure Care's team of experienced caregivers is dedicated to providing 
                                    personalized care and support to each of our clients, with a focus on enhancing 
                                    their quality of life and promoting their independence. Whether you need assistance 
                                    with daily living activities, medication management, or just a friendly companion, 
                                    we're here to help.
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7, fontSize: '1.1rem' }}>
                                    Our services are available on a flexible schedule, including live-in and hourly 
                                    options, so you can choose the level of care that best fits your needs.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={5}>
                        <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3,height: isMobile ? '300px' : '400px'}}>
                            <img src={image2} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Compassionate caregiving team" 
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Values Section */}
                <Box sx={{ mb: 8 }}>
                    <Typography variant="h3" component="h2" sx={{ textAlign: 'center', mb: 4, color: 'white',fontWeight: 'bold'}}>
                        Why Choose 24/7 Assure Care?
                    </Typography>
                    
                    <Grid container spacing={3}>
                        {values.map((value, index) => (
                            <Grid item xs={12} sm={6} md={3} key={index}>
                                <Card sx={{ height: '100%', textAlign: 'center', p: 3,transition: 'transform 0.3s, box-shadow 0.3s',
                                    '&:hover': {transform: 'translateY(-5px)',boxShadow: 4}}}>
                                    <CardContent>
                                        <Box sx={{ mb: 2 }}>
                                            {value.icon}
                                        </Box>
                                        <Typography variant="h6" component="h3" sx={{ mb: 2,color: '#1b8fc9',fontWeight: 'bold'}}>
                                            {value.title}
                                        </Typography>
                                        <Typography variant="body2">
                                            {value.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Call to Action Section */}
                <Card sx={{ borderRadius: 3, boxShadow: 3, backgroundColor: 'white', p: 4, textAlign: 'center', mb: 4 }}>
                    <CardContent>
                        <Typography variant="h4" component="h2" sx={{ mb: 3, color: '#1b8fc9', fontWeight: 'bold' }}>
                            Experience the 24/7 Assure Care Difference
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 4, maxWidth: '800px', margin: '0 auto', fontSize: '1.1rem', color: 'text.secondary' }}>
                            Our agency is founded by experienced caregivers who understand that each client requires 
                            personalized care. We take deep consideration into compatibility based on client needs 
                            and preferences. Offering 24-hour services including holidays and weekends, you and your 
                            loved one can relax knowing 24/7 Assure Care is there.
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                            <Button
                                variant="contained"
                                component={Link}
                                to="/Services"
                                sx={{
                                    
                                    backgroundColor: '#1b8fc9',
                                    '&:hover': { color: 'white', backgroundColor: '#156a9e' },
                                    fontWeight: 'bold',
                                    minWidth: '180px',
                                    py: 1,
                                    fontSize: '1.1rem'
                                }}
                                size="large"
                            >
                                Our Services
                            </Button>
                            <Button
                                variant="contained"
                                component={Link}
                                to="/Contact"
                                sx={{
                                    
                                    backgroundColor: '#1b8fc9',
                                    '&:hover': { color: 'white', backgroundColor: '#156a9e' },
                                    fontWeight: 'bold',
                                    minWidth: '180px',
                                    py: 1,
                                    fontSize: '1.1rem'
                                }}
                                size="large"
                            >
                                Contact Us
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </div>
    )
}

export default AboutUs;